import { useRef } from 'react';

/**
 * ONE SEGMENTED CONTROL FOR EVERY "WHICH VIEW" SWITCH — owner, 2026-10-08 (roster) and 2026-10-11
 * (standings). A grey track with the chosen option in white; tabs to a screen reader, and Left and
 * Right move between them. Shared so the roster and the standings cannot drift apart again: the
 * standings' own switch had become a solid navy block that matched nothing else.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** What the switch chooses, for screen readers. */
  label: string;
  options: readonly (readonly [T, string])[];
  value: T;
  onChange: (value: T) => void;
}) {
  const tabs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex w-fit gap-1 rounded-[10px] bg-light p-1"
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const at = options.findIndex(([key]) => key === value);
        const next = options[(at + (event.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length]![0];
        onChange(next);
        tabs.current[next]?.focus();
      }}
    >
      {options.map(([key, text]) => (
        <button
          key={key}
          ref={(el) => { tabs.current[key] = el; }}
          type="button"
          role="tab"
          aria-selected={value === key}
          tabIndex={value === key ? 0 : -1}
          onClick={() => onChange(key)}
          className={[
            'min-h-9 rounded-[7px] px-4 text-[14px] transition-colors duration-[120ms] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-deep',
            value === key ? 'bg-paper font-semibold text-ink shadow-sm' : 'text-ink-2 hover:bg-paper/50 hover:text-ink',
          ].join(' ')}
        >
          {text}
        </button>
      ))}
    </div>
  );
}
